import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { FieldWrapper, StepActions, PageHeader } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { ApiError } from "@interfaces/response";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

export const UsoCredito = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.uso_de_credito.code);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  const formik = useFormik({
    initialValues: {
      reason: "",
      activity: "",
    },
    validationSchema: yup.object().shape({
      reason: yup.string().required("Campo requerido"),
      activity: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        await triggerSaveUseSelected({
          loanId: application.id,
          body: {
            reason: values.reason,
            activity: values.activity,
            business_sector_id: null,
            resource_uses_identifiers: [],
          },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus?.url || "");
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "UsoCredito", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { setFieldValue, validateForm } = formik;

  const fetchLoanCreditUse = useCallback(async () => {
    try {
      setIsLoadingInitial(true);
      const loanCreditUse = await getLoanCreditUse(application.id).unwrap();
      if (loanCreditUse) {
        await setFieldValue("reason", loanCreditUse.reason || "", false);
        await setFieldValue("activity", loanCreditUse.activity || "", false);
        setTimeout(() => {
          validateForm();
        }, 200);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al consultar el destino del crédito", error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [application.id, getLoanCreditUse, setFieldValue, validateForm]);

  useEffect(() => {
    fetchLoanCreditUse();
  }, [fetchLoanCreditUse]);

  const handleBack = () => {
    if (previousStatus) {
      navigate(previousStatus.url);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Uso del crédito"
        title="¿Para qué usarás el crédito?"
        subtitle="Esta información nos ayuda a entender tu negocio y proponer las mejores condiciones."
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FieldWrapper label="Destino del crédito" full error={(formik.touched.reason && formik.errors.reason) || ""}>
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            multiline
            minRows={3}
            name="reason"
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Describe en qué se usará el monto solicitado. Ej. Compra de inventario para temporada alta, expansión de bodega…"
            inputProps={{ maxLength: 150 }}
            disabled={formik.isSubmitting || isLoadingInitial}
            sx={{
              "& .MuiOutlinedInput-root": { height: "auto" },
              "& .MuiOutlinedInput-input": { height: "auto", padding: "10px 14px" },
            }}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Actividad de la empresa"
          full
          error={(formik.touched.activity && formik.errors.activity) || ""}
        >
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            multiline
            minRows={3}
            name="activity"
            value={formik.values.activity}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="¿A qué se dedica la empresa?"
            inputProps={{ maxLength: 150 }}
            disabled={formik.isSubmitting || isLoadingInitial}
            sx={{
              "& .MuiOutlinedInput-root": { height: "auto" },
              "& .MuiOutlinedInput-input": { height: "auto", padding: "10px 14px" },
            }}
          />
        </FieldWrapper>
      </Paper>

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || formik.isSubmitting || isLoadingInitial}
      />
    </Box>
  );
};
